import NoodleAI from '@/components/Icons/NoodleAI';
import Workflow from '@/components/Icons/Workflow';
import Textbox from '@/components/Icons/Textbox';
import Database from '@/components/Icons/Database';
import Link from '@/components/Icons/Link';
import ConditionalFill from '@/components/Icons/ConditionalFill';
import MeansTest from '@/components/Icons/MoneyWavy';
import classNames from 'classnames';
import Table from '@/components/Icons/Table';
import React, { FC, Fragment, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import Spacer from '@/components/Spacer';
import FileText from '@components/Icons/FileText';
import { QuestionnaireResponse } from '@/typings/questionnaires-api-models';
import Tooltip from '@/components/Tooltip';
import ArrowsClockwise from '@components/Icons/ArrowsClockwise';
import { Questionnaire } from '@/typings/Questionnaire';
import { useEventsContext } from '@/providers/Events';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import ProgressIndicator from '@/components/ProgressIndicator';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import { ArrowSquareIn } from '@phosphor-icons/react';
import isFieldValueEmpty from '@/helpers/isFieldValueEmpty';
import s from './PopulatedFrom.module.scss';

const ICON_PROPS = { color: 'var(--color-gray-75)', size: 12 };

type ExcludedSources = 'manual' | 'unknown';
export type AllowedPopulatedFromSources = Exclude<QuestionnaireResponse['source'], ExcludedSources>;

const typeConfig: Record<
  AllowedPopulatedFromSources,{
    icon: ReactElement;
    label: {
      pending?: string;
      success?: string;
      error?: string;
      edited?: string;
    };
  }> = {
    'ai': {
      icon: <NoodleAI size={16} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill by AI agent',
        success: 'Populated by AI agent',
      },
    },
    'calculation': {
      icon: <ConditionalFill {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill by calculation',
        success: 'Populated by calculation',
      },
    },
    'conditional-fill': {
      icon: <ConditionalFill {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill by conditional fill',
        success: 'Populated by conditional autofill',
      },
    },
    'credit-report': {
      icon: <Table {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill from credit report',
        success: 'Populated from credit report',
      },
    },
    'data-reference-slug': {
      icon: <Database {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill from',
        success: 'Populated from',
      },
    },
    'field-template-key': {
      icon: <FileText {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill from form data',
        success: 'Populated from form data',
      },
    },
    'field-template-list-id-map': {
      icon: <FileText {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill from form data',
        success: 'Populated from form data',
      },
    },
    'initial-values': {
      icon: <Textbox {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill from Default value',
        success: 'Default value',
      },
    },
    'internal-data': {
      icon: <Database {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill from Internal data',
        success: 'Populated from Internal data',
      },
    },
    'linked-field': {
      icon: <Link {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill from linked field',
        success: 'Populated from linked field',
      },
    },
    'means-test-data': {
      icon: <MeansTest {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill from means test data',
        success: 'Populated from means test data',
      },
    },
    'noodle-questionnaire': {
      icon: <Workflow {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill from Glade questionnaire',
        success: 'Populated from Glade questionnaire',
      },
    },
    'workflow-context': {
      icon: <Workflow {...ICON_PROPS} />,
      label: {
        edited: 'Autofill was edited',
        error: 'Autofill data not available',
        pending: 'Autofill from workflow context',
        success: 'Populated from workflow context',
      },
    },
  };

const PopulatedFrom: FC<{
  type?: AllowedPopulatedFromSources;
  onClick?: (e: React.MouseEvent) => void;
  prominent?: boolean;
  itemId?: string;
  tooltip?: string | null;
  canReset?:boolean;
  field?: Questionnaire.FieldCommon,
}> = ({ itemId, type, onClick, prominent , tooltip = null, canReset, field}) => {
  const { emit, addListener, removeListener } = useEventsContext();
  const { formContextId, getAutofillStatus, formMethods, getResponseSource } = useQuestionnaireFormContext();
  const { userWorkflowId, isEnabled } = useQuestionnaireContext();
  const isResetAutofillEnabled = useFeatureFlag('can-use-reset-autofill');
  const [autofillStatus, setAutofillStatus] = useState<'pending' | 'success' | 'error' | 'edited'>('pending');
  const [isAutofillRerunning, setIsAutofillRerunning] = useState<boolean>(false);

  const fieldSource = getResponseSource(field?.key ?? '');
  const isfieldAutofillRun = getAutofillStatus(field?.key ?? '');

  const fieldIdRef = useRef(field?.id);
  fieldIdRef.current = field?.id;
  useEffect(() => {
    const listener = addListener('autofillRerunning', ({ status, fieldId, contextId }) => {
      if (contextId !== formContextId) return;
      if (fieldId === fieldIdRef.current) setIsAutofillRerunning(status);
    });
    return () => removeListener('autofillRerunning', listener);
  }, [addListener, removeListener, formContextId]);

  const handleRerunAutofill = useCallback(() => {
    if (field) {
      emit('reRunAutofill', {
        contextId: formContextId,
        field,
      });
    }
  }, [emit, field, formContextId]);

  useEffect(() => {
    if (field?.autofill && field?.key) {

      const fieldValue = formMethods.getValues(field?.key ?? '');

      const isEmpty = isFieldValueEmpty(fieldValue);

      if(isfieldAutofillRun) {
        if (fieldValue && !isEmpty) {
          setAutofillStatus('success');
        } else {
          setAutofillStatus('error');
        }
        if (fieldSource === 'manual') {
          setAutofillStatus('edited');
        }
      }
      else {
        setAutofillStatus('pending');
      }
    }
  }, [fieldSource, formMethods, field, isfieldAutofillRun]);

  if (!type) {
    return null;
  }

  const { icon, label } = typeConfig[type] || {};

  if (!icon || !label) {
    return null;
  }

  const Component = onClick ? 'button' : 'div';

  const TooltipOrFragment = tooltip
    ? ({ children }: { children: React.ReactElement }) => (
      <Tooltip text={tooltip}>
        {children}
      </Tooltip>
    )
    : Fragment;

  return (
    <div style={{ display: 'flex' }}>
      <TooltipOrFragment>
        <Component
          className={classNames(
            s.label,
            prominent && s.prominent,
            (autofillStatus === 'pending' && userWorkflowId) && s.labelPending,
            (autofillStatus === 'error' && userWorkflowId) && s.labelError,
            (autofillStatus === 'edited' && userWorkflowId) && s.labelEdited,
          )}
          onClick={onClick}
          style={{
            ...((onClick && { cursor: 'pointer' }) || {}),
            ...(field && canReset ? { borderBottomRightRadius: 0, borderTopRightRadius: 0 } : {}),
          }}
        >
          {icon}
          {userWorkflowId ? label[autofillStatus] : label.success}
          {type === 'data-reference-slug' && itemId ? ` ${itemId.replace(/-/g, ' ')} dataset` : ''}
          <Spacer size={8} />
        </Component>
      </TooltipOrFragment>
      {isEnabled && <>
        {field && canReset && isResetAutofillEnabled && !isAutofillRerunning && userWorkflowId && autofillStatus === 'pending'
          ? (
            <div className={s.autofillBtn} onClick={handleRerunAutofill}>
              <ArrowSquareIn size={12} weight="fill" color="var(--color-gray-75)" />
            </div>
          )
          : null}
        {field && canReset && isResetAutofillEnabled && !isAutofillRerunning && userWorkflowId && autofillStatus !== 'pending'
          ? (
            <Tooltip text="Re-run autofill">
              <div className={s.resetBtn} onClick={handleRerunAutofill}>
                <ArrowsClockwise size={12} weight="fill" color="var(--color-gray-75)" />
              </div>
            </Tooltip>
          )
          : null}
        {field && canReset && isResetAutofillEnabled && isAutofillRerunning && userWorkflowId
          ? (
            <div className={classNames(s.loadingIndicator, autofillStatus === 'pending' && s.autofillBtn)}>
              <ProgressIndicator size={12} />
            </div>
          )
          : null}
      </>}
    </div>
  );
};

export default PopulatedFrom;
